import type { RecordId, VersionId } from "@/domain/records/types";

/**
 * Repository interface for Report data access
 * Clean Architecture - Infrastructure Layer
 */
export interface IReportRepository {
  getQaReport(recordId: RecordId, version?: VersionId): Promise<string | null>;
  getPersonaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getJtbdReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getCustomReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getGeneralReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;

  updateQaReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void>;
  updatePersonaReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void>;
  updateJtbdReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void>;
  updateCustomReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void>;

  getPublicQaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getPublicPersonaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getPublicJtbdReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
  getPublicCustomReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null>;
}

/**
 * Report Repository implementation
 * Uses Insight API for markdown report access
 */
export class ReportRepository implements IReportRepository {
  constructor(
    private readonly apiBaseUrl: string,
    private readonly insightApiUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async getQaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getReport("qa-report", recordId, version);
  }

  async getPersonaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getReport("persona-report", recordId, version);
  }

  async getJtbdReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getReport("jtbd-markdown-report", recordId, version);
  }

  async getCustomReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getReport("custom-report", recordId, version);
  }

  async getGeneralReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    // General reports are accessed differently - through the full record endpoint
    try {
      const url = version
        ? `${this.insightApiUrl}/insight/${recordId}/?version=${version}`
        : `${this.insightApiUrl}/insight/${recordId}/`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch general report: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data.general_report || null;
    } catch (error) {
      console.error("ReportRepository.getGeneralReport error:", error);
      return null;
    }
  }

  async updateQaReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void> {
    await this.updateReport("qa-report", recordId, content, version);
  }

  async updatePersonaReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void> {
    await this.updateReport("persona-report", recordId, content, version);
  }

  async updateJtbdReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void> {
    await this.updateReport("jtbd-markdown-report", recordId, content, version);
  }

  async updateCustomReport(
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void> {
    await this.updateReport("custom-report", recordId, content, version);
  }

  async getPublicQaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getPublicReport("qa-report", recordId, version);
  }

  async getPublicPersonaReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getPublicReport("persona-report", recordId, version);
  }

  async getPublicJtbdReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getPublicReport("jtbd-markdown-report", recordId, version);
  }

  async getPublicCustomReport(
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    return this.getPublicReport("custom-report", recordId, version);
  }

  /**
   * Generic method to get a report
   */
  private async getReport(
    endpoint: string,
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    try {
      const url = version
        ? `${this.insightApiUrl}/insight/${recordId}/${endpoint}?version=${version}`
        : `${this.insightApiUrl}/insight/${recordId}/${endpoint}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Report not found
        }
        throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
      }

      return await response.text();
    } catch (error) {
      console.error(`ReportRepository.getReport(${endpoint}) error:`, error);
      return null;
    }
  }

  /**
   * Generic method to update a report
   */
  private async updateReport(
    endpoint: string,
    recordId: RecordId,
    content: string,
    version?: VersionId
  ): Promise<void> {
    try {
      const url = version
        ? `${this.insightApiUrl}/insight/${recordId}/${endpoint}?version=${version}`
        : `${this.insightApiUrl}/insight/${recordId}/${endpoint}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ markdown_content: content }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update ${endpoint}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`ReportRepository.updateReport(${endpoint}) error:`, error);
      throw new Error(
        `Failed to update report: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Generic method to get a public report
   */
  private async getPublicReport(
    endpoint: string,
    recordId: RecordId,
    version?: VersionId
  ): Promise<string | null> {
    try {
      const url = version
        ? `${this.insightApiUrl}/public/${recordId}/${endpoint}?version=${version}`
        : `${this.insightApiUrl}/public/${recordId}/${endpoint}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Public report not found
        }
        throw new Error(
          `Failed to fetch public ${endpoint}: ${response.statusText}`
        );
      }

      return await response.text();
    } catch (error) {
      console.error(
        `ReportRepository.getPublicReport(${endpoint}) error:`,
        error
      );
      return null;
    }
  }
}
